import { inject, Service } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthStore } from '@vantage-web/core-data';

/** Top-level lazy portal routes and the AuthStore flag that grants each one. */
const PORTAL_ROUTES = ['oip', 'sub', 'client'] as const;

/**
 * Background-preloads only the lazy bundle for the portal the signed-in user can actually
 * reach, instead of all four (which `PreloadAllModules` would do). A single-portal user
 * never visits the other three areas, so eagerly downloading their bundles is wasted
 * bandwidth/CPU — those load lazily on demand if ever navigated to. Non-portal routes
 * (e.g. `/auth`) preload normally. (Phase P — preloading.)
 */
@Service()
export class ActivePortalPreloadStrategy implements PreloadingStrategy {
  readonly #auth = inject(AuthStore);

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    const path = route.path;
    if (path && (PORTAL_ROUTES as readonly string[]).includes(path)) {
      return this.#canReachPortal(path) ? load() : of(null);
    }
    return load();
  }

  #canReachPortal(path: string): boolean {
    switch (path) {
      case 'oip': return this.#auth.isOipUser() || this.#auth.isCorpUser();
      case 'sub': return this.#auth.isSubUser() || this.#auth.hasSubProxyGrant();
      case 'client': return this.#auth.isClientUser();
      default: return false;
    }
  }
}
